/* eslint-disable no-plusplus */
import React, { useState, useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Button,
  Tooltip,
  TextField,
  Stack,
  useTheme,
  useMediaQuery,
  Divider,
  CircularProgress,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import axios, { getErrorMessage } from 'utils/axios';
import CRUD from 'utils/Crud';

ImportCruiseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleComplete: PropTypes.func.isRequired,
};

const MAX_MESSAGE_LENGTH = 280;
const MAX_CATEGORY_LENGTH = 50;
const MAX_CRUISES_DISPLAY = 5000;
const CRUISE_TYPES = [
  { value: 'giftcard', label: 'Giftcard' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'general', label: 'General' },
];

const isValidCruise = (obj) => {
  if (
    typeof obj === 'object' &&
    obj !== null &&
    typeof obj.message === 'string' &&
    typeof obj.category === 'string' &&
    typeof obj.type === 'string' &&
    typeof obj.rating === 'number' &&                    // rating is a number
    obj.message.length <= MAX_MESSAGE_LENGTH &&
    obj.category.length <= MAX_CATEGORY_LENGTH &&
    CRUISE_TYPES.some(typeObj => typeObj.value === obj.type) &&      // validate type
    obj.rating >= 1 && obj.rating <= 4                          // rating within bounds
  ) {
    return true;
  }
  return false;
};

export default function ImportCruiseDialog({ open, handleClose, handleComplete }) {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  // States
  const [batchName, setBatchName] = useState('');
  const [file, setFile] = useState(null);
  const [cruises, setCruises] = useState([]); // parsed cruises
  const [selectedCruises, setSelectedCruises] = useState(new Set()); // set of indexes
  const [loading, setLoading] = useState(false);
  const [parsingError, setParsingError] = useState(null);
  const inputFileRef = useRef(null);
  const dragCounter = useRef(0);
  const [isDragActive, setIsDragActive] = useState(false);

  // Reset all states when dialog closes
  useEffect(() => {
    if (!open) {
      setBatchName('');
      setFile(null);
      setCruises([]);
      setSelectedCruises(new Set());
      setParsingError(null);
      setLoading(false);
      dragCounter.current = 0;
      setIsDragActive(false);
    }
  }, [open]);

  // Drag & Drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    setIsDragActive(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      setIsDragActive(false);
      dragCounter.current = 0;
    }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    dragCounter.current = 0;

    const droppedFiles = e.dataTransfer.files;
    if (!droppedFiles || droppedFiles.length === 0) return;

    if (droppedFiles.length > 1) {
      enqueueSnackbar('Please upload only a single JSON file', { variant: 'warning' });
      return;
    }

    const droppedFile = droppedFiles[0];
    if (!droppedFile.name.toLowerCase().endsWith('.json')) {
      enqueueSnackbar('Only JSON files are accepted', { variant: 'error' });
      return;
    }

    handleFile(droppedFile);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files.length === 0) return;
    const selectedFile = e.target.files[0];

    if (!selectedFile.name.toLowerCase().endsWith('.json')) {
      enqueueSnackbar('Only JSON files are accepted', { variant: 'error' });
      e.target.value = null;
      return;
    }
    handleFile(selectedFile);
  };

  // Parses and validates the JSON file and updates cruises state
  const handleFile = (selectedFile) => {
    setParsingError(null);
    setFile(selectedFile);
    setCruises([]);
    setSelectedCruises(new Set());
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target.result);

        if (!Array.isArray(json)) throw new Error('JSON must be an array');

        if (json.length === 0) {
          throw new Error('JSON array is empty');
        }

        if (json.length > 5000) {
          // sanity check for too large files
          enqueueSnackbar('File is too large. Please upload smaller JSON.', { variant: 'warning' });
          setFile(null);
          return;
        }

        // Validate each item
        for (let i = 0; i < json.length; i++) {
          if (!isValidCruise(json[i])) {
            throw new Error(
              `Invalid item at index ${i}. Each item must have: 
                - message (string, max length ${MAX_MESSAGE_LENGTH}),
                - category (string, max length ${MAX_CATEGORY_LENGTH}),
                - type (one of: ${CRUISE_TYPES.map(t => t.value).join(', ')}), 
                - rating (integer number between 1 and 4).
                 - Messgae: ${json[i]?.message || ''} `
            );
          }          
        }

        // Limit preview to MAX_CRUISES_DISPLAY to avoid UI lag
        const previewList = json.slice(0, MAX_CRUISES_DISPLAY);

        setCruises(previewList);
        // Initialize selection: all selected by default
        setSelectedCruises(new Set(previewList.map((_, idx) => idx)));
        enqueueSnackbar(`Loaded ${previewList.length} cruises. Showing preview`, { variant: 'success' });

        // Note: any cruises beyond preview limit will be ignored for selection and preview

      } catch (error) {
        setParsingError(error.message);
        setFile(null);
        setCruises([]);
        setSelectedCruises(new Set());
      }
    };
    reader.onerror = () => {
      enqueueSnackbar('Failed to read file', { variant: 'error' });
      setFile(null);
      setCruises([]);
      setSelectedCruises(new Set());
    };
    reader.readAsText(selectedFile);
  };

  // Selection toggling handler
  const toggleSelection = (idx) => {
    setSelectedCruises((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) {
        newSet.delete(idx);
      } else {
        newSet.add(idx);
      }
      return newSet;
    });
  };

  // Submit upload
  const onSubmit = async () => {
    // validations before submit
    if (!file) {
      enqueueSnackbar('Please upload a JSON file first', { variant: 'warning' });
      return;
    }
    if (!batchName.trim()) {
      enqueueSnackbar('Please enter a batch name', { variant: 'warning' });
      return;
    }
    if (cruises.length === 0) {
      enqueueSnackbar('No cruises data to upload', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      // Calculate indexes of deselected (skipped) cruises in preview list
      const skippedIndexesPreview = [];
      for (let i = 0; i < cruises.length; i++) {
        if (!selectedCruises.has(i)) {
          skippedIndexesPreview.push(i);
        }
      }

      // We send to server:
      // - The uploaded file as FormData under key 'file'
      // - The batchName
      // - Skipped cruise indices (only previewed)
      // Note: Assuming server can handle skipped cruises by preview index
      const formData = new FormData();
      formData.append('file', file);
      formData.append('batchName', batchName);
      formData.append('skippedCruises', JSON.stringify(skippedIndexesPreview));

      // Adjust URL as needed
      await axios.post(CRUD.cruise.import, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      enqueueSnackbar('Upload successful!', { variant: 'success' });
      // Reset all form states
      setBatchName('');
      setFile(null);
      setCruises([]);
      setSelectedCruises(new Set());
      setParsingError(null);
      handleComplete();
      handleClose();
    } catch (error) {
      enqueueSnackbar(`Upload failed: ${getErrorMessage(error)}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Memoized preview list items to prevent excessive re-renders
  const cruiseItems = useMemo(() => cruises.map((cruise, idx) => (
      <ListItem
        key={idx}
        button
        dense
        divider
        onClick={() => toggleSelection(idx)}
        sx={{
          bgcolor: selectedCruises.has(idx)
            ? theme.palette.action.selected
            : 'inherit',
          '&:hover': { bgcolor: theme.palette.action.hover },
          cursor: 'pointer',
          py: 0.5,
          px: 1,
        }}
        secondaryAction={
          <Checkbox
            edge="end"
            checked={selectedCruises.has(idx)}
            tabIndex={-1}
            disableRipple
            onChange={() => toggleSelection(idx)}
            inputProps={{ 'aria-labelledby': `cruise-item-${idx}` }}
          />
        }
      >
        <ListItemText
          id={`cruise-item-${idx}`}
          primary={
            <Typography
              noWrap
              sx={{ maxWidth: '80vw', fontWeight: 600 }}
              title={cruise.message}
            >
              {cruise.message}
            </Typography>
          }
          secondary={
            <Typography
              noWrap
              sx={{ maxWidth: '80vw' }}
              color="text.secondary"
              title={cruise.category}
            >
              Category: {cruise.category}
            </Typography>
          }
        />
      </ListItem>
    )), [cruises, selectedCruises, theme.palette.action.hover, theme.palette.action.selected]);

  // Styles for the drag area (responsive)
  const dragAreaBaseStyles = {
    border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
    borderRadius: 2,
    p: 4,
    textAlign: 'center',
    cursor: 'pointer',
    color: isDragActive ? theme.palette.primary.main : theme.palette.text.secondary,
    userSelect: 'none',
    transition: 'border-color 0.3s, color 0.3s',
  };

  return (
    <Dialog
      open={open}
      fullScreen
      onClose={() => {
        if (!loading) {
          handleClose();
        }
      }}
      PaperProps={{ sx: { bgcolor: theme.palette.background.default } }}
      aria-labelledby="import-cruise-dialog-title"
      disableEscapeKeyDown={loading}
    >
      {/* AppBar */}
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: { xs: 1, sm: 3 },
            py: { xs: 1, sm: 2 },
            minHeight: 64,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold" lineHeight={1.1}>
              Import Cruise
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Upload a JSON file and preview cruises before import
            </Typography>
          </Box>

          <Tooltip title="Close">
            <span>
              {/* span wrapper prevents disabled tooltip warning */}
              <IconButton
                color="inherit"
                onClick={() => {
                  if (!loading) {
                    handleClose();
                  }
                }}
                disabled={loading}
                aria-label="close dialog"
                size="large"
              >
                <Icon icon="mdi:close" fontSize={28} />
              </IconButton>
            </span>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Divider />

      {/* Main Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: isSmDown ? 'column' : 'row',
          justifyContent: 'center',
          alignItems: 'flex-start',
          height: 'calc(100vh - 112px)', // subtract AppBar & padding
          p: 3,
          gap: 3,
          bgcolor: theme.palette.background.paper,
          overflow: 'hidden',
        }}
      >
        {/* Left: Upload & Batch Name */}
        <Box
          sx={{
            flex: '1 1 300px',
            maxWidth: 480,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {/* Batch Name */}
          <TextField
            label="Batch Name"
            value={batchName}
            onChange={(e) => setBatchName(e.target.value)}
            variant="outlined"
            fullWidth
            disabled={loading}
          />

          {/* Drag and Drop upload area */}
          <Paper
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            sx={dragAreaBaseStyles}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                inputFileRef.current?.click();
                e.preventDefault();
              }
            }}
          >
            <Typography variant="body1" sx={{ mb: 1 }}>
              Drag and drop JSON file here, or click to select file
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Only JSON files are accepted.
            </Typography>
            <input
              ref={inputFileRef}
              type="file"
              accept=".json,application/json"
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
              disabled={loading}
            />
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => inputFileRef.current?.click()}
              disabled={loading}
            >
              Select JSON File
            </Button>
          </Paper>

          {/* Parsing error */}
          {parsingError && (
            <Typography color="error" variant="body2" mt={1}>
              {parsingError}
            </Typography>
          )}

          {/* Show file name and count */}
          {file && (
            <Typography variant="body2" color="text.secondary" mt={1} noWrap>
              Loaded file: <b>{file.name}</b> ({cruises.length} cruises previewed)
            </Typography>
          )}
        </Box>

        {/* Right: Preview of cruises */}
        <Box
          sx={{
            flex: '1 1 400px',
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 1,
          }}
          aria-label="Cruise preview list"
        >
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Cruise Preview ({cruises.length} items)
          </Typography>

          {cruises.length === 0 ? (
            <Typography
              color="text.secondary"
              sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              No data loaded. Please upload a JSON file.
            </Typography>
          ) : (
            <List
              dense
              sx={{
                overflowY: 'auto',
                flexGrow: 1,
                bgcolor: theme.palette.background.paper,
              }}
            >
              {cruiseItems}
            </List>
          )}
        </Box>
      </Box>

      {/* Footer Actions */}
      <Box
        sx={{
          p: 3,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            if (!loading) {
              handleClose();
            }
          }}
          disabled={loading}
          size="large"
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={loading || cruises.length === 0 || !batchName.trim()}
          size="large"
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Upload'}
        </Button>
      </Box>
    </Dialog>
  );
}
